/**
 * The sound a reminder makes.
 *
 * Speech is the one thing on iOS a timer can never do: `speak()` is gated to the call stack of
 * a real gesture, and anything scheduled is dropped without a sound or an error. Decoded audio
 * is not. One `resume()` inside a tap leaves the context running, and buffers scheduled from a
 * timer minutes later play normally — which is why reminders live here now rather than in
 * `voice.ts`.
 *
 * Still only while the app is on screen: a backgrounded page is suspended and plays nothing.
 * The push notification carries the system sound for everything else.
 */

import { report } from "./report";

export type ChimeTier = "headsUp" | "getReady" | "go";

export const CHIME_TIERS: ChimeTier[] = ["headsUp", "getReady", "go"];

export const CHIME_LABELS: Record<ChimeTier, string> = {
	headsUp: "Heads up",
	getReady: "Get ready",
	go: "Time to go",
};

/** Drop a file at any of these to replace the synthesised tone. A missing one is not an error. */
const SAMPLE_URLS: Record<ChimeTier, string> = {
	headsUp: "/audio/heads-up.wav",
	getReady: "/audio/get-ready.wav",
	go: "/audio/go.wav",
};

/**
 * The tones used until a file is dropped in.
 *
 * The last tier is meant to be unmissable without being an alarm, so it gets more notes, a
 * rising major triad and one repeat — never a harder edge or more volume. Speed, a buzzing
 * timbre and clashing intervals are what make a sound frightening; none of those is here.
 */
const NOTES: Record<ChimeTier, Array<{ hz: number; atSeconds: number; gain: number; seconds: number }>> = {
	headsUp: [{ hz: 659.25, atSeconds: 0, gain: 0.16, seconds: 0.9 }],
	getReady: [
		{ hz: 659.25, atSeconds: 0, gain: 0.2, seconds: 0.7 },
		{ hz: 880.0, atSeconds: 0.18, gain: 0.2, seconds: 0.9 },
	],
	go: [
		{ hz: 587.33, atSeconds: 0, gain: 0.26, seconds: 0.6 },
		{ hz: 739.99, atSeconds: 0.16, gain: 0.26, seconds: 0.6 },
		{ hz: 880.0, atSeconds: 0.32, gain: 0.3, seconds: 1.2 },
		{ hz: 880.0, atSeconds: 0.86, gain: 0.22, seconds: 1.2 },
	],
};

let context: AudioContext | null = null;
let output: AudioNode | null = null;

/** How long a tier rings for, so a voice can follow it rather than talk over it. */
export function chimeSeconds(tier: ChimeTier): number {
	const sample = samples.get(tier);
	if (sample) {
		return sample.duration;
	}
	return NOTES[tier].reduce((longest, note) => Math.max(longest, note.atSeconds + note.seconds), 0);
}
/** A tier present with a null value has been looked for and has no file; it uses the tones. */
const samples = new Map<ChimeTier, AudioBuffer | null>();
let loading: Promise<void> | null = null;
let sessionWatched = false;

/** Safari 16.4 and up. Experimental, so it is felt for rather than assumed. */
interface AudioSessionLike extends EventTarget {
	type: string;
	readonly state: string;
}

function audioSession(): AudioSessionLike | null {
	return (navigator as Navigator & { audioSession?: AudioSessionLike }).audioSession ?? null;
}

/** Shared, so a spoken clip plays through the same unlock the chimes rely on. */
export function audioContext(): AudioContext | null {
	if (context) {
		return context;
	}
	const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
	if (!Ctor) {
		return null;
	}
	context = new Ctor();
	output = null;
	return context;
}

/**
 * Everything the app plays goes out through here.
 *
 * A web app has no way to duck other audio — `duckOthers` is native only — so a reminder lands at
 * whatever volume his music is already playing at and has to carry on its own. The compressor
 * flattens the peaks first, so the gain lifts the whole clip rather than just clipping the loud
 * parts of it.
 */
const REMINDER_GAIN = 2.4;

export function reminderOutput(ctx: AudioContext): AudioNode {
	if (output) {
		return output;
	}
	const compressor = ctx.createDynamicsCompressor();
	compressor.threshold.value = -24;
	compressor.knee.value = 12;
	compressor.ratio.value = 6;
	compressor.attack.value = 0.003;
	compressor.release.value = 0.2;
	const gain = ctx.createGain();
	gain.gain.value = REMINDER_GAIN;
	compressor.connect(gain);
	gain.connect(ctx.destination);
	output = compressor;
	return compressor;
}

/**
 * Ask to be heard alongside his music, not instead of it.
 *
 * `transient` mixes: other audio ducks for the moment and carries on. The alternatives were both
 * tried on a real phone and both lost his music — `playback` claimed the media role outright, and
 * `transient-solo` interrupted cleanly but nothing resumed, because only a native app can set the
 * flag that says "you may carry on" and Web Audio cannot.
 *
 * Claimed per sound rather than on every touch, so browsing the app never touches the speaker.
 */
export function claimAudioSession(): void {
	const session = audioSession();
	if (!session) {
		return;
	}
	try {
		session.type = "transient";
	} catch {
		// An older WebKit that exposes the object but not the setter.
	}
	if (!sessionWatched) {
		sessionWatched = true;
		session.addEventListener("statechange", () => report("audio-session", { state: session.state, type: session.type }));
	}
}

/**
 * Drop the claim once the sound is over.
 *
 * The type only, never the context: suspending it would hand the speaker back but leave the next
 * reminder having to resume from a timer, and a reminder is not worth that risk. Mixing means
 * there is nothing to hand back anyway.
 */
export function releaseAudioSession(afterSeconds: number): void {
	const session = audioSession();
	if (!session) {
		return;
	}
	window.setTimeout(
		() => {
			try {
				session.type = "auto";
				report("audio-release", { type: session.type, state: context?.state ?? null });
			} catch {
				// Nothing to do: the claim will simply outlive the sound.
			}
		},
		Math.max(1, afterSeconds) * 1000 + 400,
	);
}

/** Everything the platform will admit to. The ringer switch itself is not observable. */
export function audioDiagnostics(): Record<string, unknown> {
	const session = audioSession();
	return {
		supported: audioContext() != null,
		state: context?.state ?? null,
		sampleRate: context?.sampleRate ?? null,
		currentTime: context ? Math.round(context.currentTime * 1000) / 1000 : null,
		sessionSupported: session != null,
		sessionType: session?.type ?? null,
		sessionState: session?.state ?? null,
		sampled: CHIME_TIERS.filter((tier) => samples.get(tier) != null),
		visibility: document.visibilityState,
	};
}

/**
 * Unlock from a real tap.
 *
 * Only the resume has to happen inside the gesture. iOS also suspends the context when the app
 * goes away, so this runs again on every touch rather than once at startup.
 */
export function unlockAudio(): void {
	const ctx = audioContext();
	if (!ctx) {
		return;
	}
	if (ctx.state !== "running") {
		const startedAt = Date.now();
		const before = ctx.state;
		void ctx
			.resume()
			.then(() => report("audio-unlock", { before, resumeMs: Date.now() - startedAt, ...audioDiagnostics() }))
			.catch((cause) => report("audio-unlock", { before, error: String(cause), ...audioDiagnostics() }));
	}
	void loadSamples();
}

/** Decoded up front, so the moment a reminder is due there is nothing left to fetch. */
export function loadSamples(): Promise<void> {
	const ctx = audioContext();
	if (!ctx) {
		return Promise.resolve();
	}
	loading ??= Promise.all(
		CHIME_TIERS.map(async (tier) => {
			try {
				const response = await fetch(SAMPLE_URLS[tier]);
				if (!response.ok) {
					samples.set(tier, null);
					return;
				}
				samples.set(tier, await ctx.decodeAudioData(await response.arrayBuffer()));
			} catch {
				// An unreadable or absent file just means this tier keeps its tone.
				samples.set(tier, null);
			}
		}),
	).then(() => undefined);
	return loading;
}

function playTones(ctx: AudioContext, tier: ChimeTier): void {
	for (const note of NOTES[tier]) {
		const at = ctx.currentTime + note.atSeconds;
		const oscillator = ctx.createOscillator();
		const gain = ctx.createGain();
		oscillator.type = "sine";
		oscillator.frequency.value = note.hz;
		// Struck and left to ring: an instant start clicks, and a sustained level is a buzzer.
		gain.gain.setValueAtTime(0.0001, at);
		gain.gain.exponentialRampToValueAtTime(note.gain, at + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, at + note.seconds);
		oscillator.connect(gain).connect(reminderOutput(ctx));
		oscillator.start(at);
		oscillator.stop(at + note.seconds + 0.05);
	}
}

/** Sound a reminder. Safe to call from a timer, which is the entire point of it. */
export function playChime(tier: ChimeTier): void {
	const ctx = audioContext();
	if (!ctx) {
		report("chime", { tier, supported: false });
		return;
	}
	claimAudioSession();
	releaseAudioSession(4);
	// A context suspended behind the app's back stays silent, and the resume is asynchronous,
	// so the sound is scheduled after it rather than alongside it.
	const start = ctx.state === "running" ? Promise.resolve() : ctx.resume().catch(() => undefined);
	void start.then(() => {
		const sample = samples.get(tier);
		const startedAt = ctx.currentTime;
		let ended = false;
		if (sample) {
			const source = ctx.createBufferSource();
			source.buffer = sample;
			source.onended = () => {
				ended = true;
			};
			source.connect(reminderOutput(ctx));
			source.start();
		} else {
			playTones(ctx, tier);
		}
		report("chime", { tier, sampled: sample != null, ...audioDiagnostics() });
		// A clock that has not moved is a graph that never ran, which is the difference between
		// "played and you did not hear it" and "never played at all".
		window.setTimeout(() => {
			report("chime-after", {
				tier,
				ended,
				advancedSeconds: Math.round((ctx.currentTime - startedAt) * 1000) / 1000,
				...audioDiagnostics(),
			});
		}, 2500);
	});
}
